import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { 
  Plus, 
  Trophy, 
  ChevronRight, 
  Globe, 
  Lock,
  Pencil,
  Trash2,
  Loader2,
  Settings2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Championships() {
  const navigate = useNavigate();
  const [championships, setChampionships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChamp, setEditingChamp] = useState(null);
  const [deleteChamp, setDeleteChamp] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_public: false,
    total_races: 4,
    races_to_count: 3,
    use_attendance_bonus: false,
    attendance_model_id: ''
  });
  const [attendanceModels, setAttendanceModels] = useState([]);

  const fetchChampionships = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/championships`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setChampionships(data);
      }
    } catch (error) {
      console.error('Error fetching championships:', error);
      toast.error('Error al cargar campeonatos');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceModels = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/attendance-models`, {
        credentials: 'include',
        headers
      });
      if (response.ok) {
        const data = await response.json();
        setAttendanceModels(data);
      }
    } catch (error) {
      console.error('Error fetching attendance models:', error);
    }
  };

  useEffect(() => {
    fetchChampionships();
    fetchAttendanceModels();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };
      
      const url = editingChamp 
        ? `${API}/championships/${editingChamp.championship_id}`
        : `${API}/championships`;
      
      const response = await fetch(url, {
        method: editingChamp ? 'PUT' : 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al guardar');
      }

      toast.success(editingChamp ? 'Campeonato actualizado' : 'Campeonato creado');
      setDialogOpen(false);
      setEditingChamp(null);
      setFormData({ name: '', description: '', is_public: false, total_races: 4, races_to_count: 3, use_attendance_bonus: false, attendance_model_id: '' });
      fetchChampionships();
    } catch (error) {
      toast.error(String(error?.message || 'Error'));
    }
  };

  const handleDelete = async () => {
    if (!deleteChamp) return;
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await fetch(`${API}/championships/${deleteChamp.championship_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers
      });

      if (!response.ok) {
        throw new Error('Error al eliminar');
      }

      toast.success('Campeonato eliminado');
      setDeleteChamp(null);
      fetchChampionships();
    } catch (error) {
      toast.error(String(error?.message || 'Error'));
    }
  };

  const openEditDialog = (champ) => {
    setEditingChamp(champ);
    setFormData({
      name: champ.name,
      description: champ.description || '',
      is_public: champ.is_public,
      total_races: champ.total_races || 4,
      races_to_count: champ.races_to_count || 3,
      use_attendance_bonus: champ.use_attendance_bonus || false,
      attendance_model_id: champ.attendance_model_id || ''
    });
    setDialogOpen(true);
  };

  const openNewDialog = () => {
    setEditingChamp(null);
    setFormData({ name: '', description: '', is_public: false, total_races: 4, races_to_count: 3 });
    setDialogOpen(true);
  };

  // Calculate valid races_to_count options based on total_races
  const getValidRacesToCount = () => {
    const options = [];
    for (let i = 1; i <= formData.total_races; i++) {
      options.push(i);
    }
    return options;
  };

  // Update races_to_count if total_races changes
  const handleTotalRacesChange = (value) => {
    const newTotal = parseInt(value);
    let newRacesToCount = formData.races_to_count;
    // Default: count all minus 1 (1 discard)
    if (newRacesToCount > newTotal) {
      newRacesToCount = Math.max(1, newTotal - 1);
    }
    setFormData({ ...formData, total_races: newTotal, races_to_count: newRacesToCount });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="championships-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-2xl uppercase">Campeonatos</h1>
            <p className="text-muted-foreground text-sm">
              Gestiona tus campeonatos AECAR
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog} data-testid="create-championship-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Campeonato
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase">
                  {editingChamp ? 'Editar Campeonato' : 'Nuevo Campeonato'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre</Label>
                  <Input
                    id="name"
                    placeholder="Ej: Campeonato 1/8 Gas 2025"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="championship-name-input"
                    className="bg-black/20 border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción (opcional)</Label>
                  <Textarea
                    id="description"
                    placeholder="Descripción del campeonato..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    data-testid="championship-description-input"
                    className="bg-black/20 border-white/10"
                  />
                </div>
                
                {/* Configuración de pruebas */}
                <div className="p-4 rounded-lg bg-black/20 border border-white/10 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                    <Settings2 className="w-4 h-4" />
                    Configuración de Pruebas
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nº de Pruebas</Label>
                      <Select
                        value={formData.total_races.toString()}
                        onValueChange={handleTotalRacesChange}
                      >
                        <SelectTrigger className="bg-black/30 border-white/10" data-testid="total-races-select">
                          <SelectValue placeholder="Pruebas" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-white/10">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                            <SelectItem key={n} value={n.toString()}>{n} pruebas</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Pruebas que Cuentan</Label>
                      <Select
                        value={formData.races_to_count.toString()}
                        onValueChange={(v) => setFormData({ ...formData, races_to_count: parseInt(v) })}
                      >
                        <SelectTrigger className="bg-black/30 border-white/10" data-testid="races-to-count-select">
                          <SelectValue placeholder="Cuentan" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-white/10">
                          {getValidRacesToCount().map((n) => (
                            <SelectItem key={n} value={n.toString()}>{n} mejores</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {formData.total_races - formData.races_to_count === 0 
                      ? 'Sin descartes - todas las pruebas cuentan'
                      : `${formData.total_races - formData.races_to_count} descarte(s) - se eliminan los peores resultados`
                    }
                  </p>
                </div>

                {/* Attendance Bonus Section */}
                <div className="space-y-3 pt-2 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="use_attendance">Puntos por Asistencia</Label>
                      <p className="text-xs text-muted-foreground">
                        Añade puntos extra según carreras disputadas
                      </p>
                    </div>
                    <Switch
                      id="use_attendance"
                      checked={formData.use_attendance_bonus}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        use_attendance_bonus: checked,
                        attendance_model_id: checked ? formData.attendance_model_id : ''
                      })}
                      data-testid="attendance-bonus-switch"
                    />
                  </div>
                  
                  {formData.use_attendance_bonus && (
                    <div className="space-y-2">
                      <Label>Modelo de Asistencia</Label>
                      <Select
                        value={formData.attendance_model_id}
                        onValueChange={(v) => setFormData({ ...formData, attendance_model_id: v })}
                      >
                        <SelectTrigger className="bg-black/30 border-white/10" data-testid="attendance-model-select">
                          <SelectValue placeholder="Selecciona un modelo" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-white/10">
                          {attendanceModels.map((model) => (
                            <SelectItem key={model.model_id} value={model.model_id}>
                              {model.name}
                              {model.is_default && ' (Estándar)'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {attendanceModels.length === 0 && (
                        <p className="text-xs text-amber-400">
                          No hay modelos de asistencia. Un administrador debe crearlos primero.
                        </p>
                      )}
                      {formData.attendance_model_id && (
                        <div className="bg-black/20 p-2 rounded text-xs">
                          {attendanceModels.find(m => m.model_id === formData.attendance_model_id)?.rules?.map((r, i) => (
                            <span key={i} className="mr-3">
                              {r.min_races}+ carreras = {r.bonus_points} pts
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="is_public">Clasificación Pública</Label>
                    <p className="text-xs text-muted-foreground">
                      Permite compartir el enlace de la clasificación
                    </p>
                  </div>
                  <Switch
                    id="is_public"
                    checked={formData.is_public}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                    data-testid="championship-public-switch"
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="submit" className="flex-1" data-testid="save-championship-btn">
                    {editingChamp ? 'Guardar Cambios' : 'Crear Campeonato'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Championships List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        ) : championships.length === 0 ? (
          <Card className="bg-card/50 border-white/10">
            <CardContent className="py-20 text-center">
              <Trophy className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-xl uppercase mb-2">Sin Campeonatos</h3>
              <p className="text-muted-foreground mb-6">
                Crea tu primer campeonato para empezar
              </p>
              <Button onClick={openNewDialog} data-testid="create-first-championship-btn">
                <Plus className="w-4 h-4 mr-2" />
                Crear Campeonato
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {championships.map((champ) => (
              <Card 
                key={champ.championship_id} 
                className="bg-card/50 border-white/10 hover:border-primary/30 transition-colors"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center">
                        <Trophy className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-bold text-lg truncate">
                          {champ.name}
                        </h3>
                        {champ.is_public ? (
                          <Globe className="w-4 h-4 text-success flex-shrink-0" />
                        ) : (
                          <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>{champ.total_races || 4} pruebas</span>
                        <span>•</span>
                        <span>{champ.races_to_count || 3} cuentan</span>
                        <span>•</span>
                        <span>{(champ.total_races || 4) - (champ.races_to_count || 3)} descarte(s)</span>
                      </div>
                      {champ.description && (
                        <p className="text-sm text-muted-foreground truncate mt-1">
                          {champ.description}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(champ)}
                        data-testid={`edit-championship-${champ.championship_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteChamp(champ)}
                        data-testid={`delete-championship-${champ.championship_id}`}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-white/10"
                        onClick={() => navigate(`/campeonatos/${champ.championship_id}`)}
                        data-testid={`view-championship-${champ.championship_id}`}
                      >
                        Ver <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteChamp} onOpenChange={() => setDeleteChamp(null)}>
          <AlertDialogContent className="bg-card border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-heading uppercase">
                Eliminar Campeonato
              </AlertDialogTitle>
              <AlertDialogDescription>
                ¿Estás seguro de eliminar "{deleteChamp?.name}"? 
                Esta acción eliminará también todos los resultados asociados.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/10">Cancelar</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive hover:bg-destructive/90"
                data-testid="confirm-delete-championship-btn"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
